import { useState } from "react";
import { NavLink } from "react-router-dom";
import { LogOut, Menu, Settings, UserCircle } from "lucide-react";

import WhaZagoLogo from "@/components/brand/WhaZagoLogo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

// Plateforme orientée revendeurs : le flux d'achat client a été retiré du site
// principal. Le menu ne garde que le profil (et le panel admin pour l'admin).
const navItems = [
  { key: "profile", to: "/profile", label: "Profil", icon: UserCircle },
];

function SidebarNav({ user, onLogout, mobile = false, onNavClick }) {
  const prefix = mobile ? "mobile-" : "";
  const items = user?.is_admin
    ? [...navItems, { key: "admin", to: "/admin", label: "Admin Panel", icon: Settings }]
    : navItems;

  return (
    <nav className="flex flex-col gap-1" data-testid={mobile ? "mobile-navigation" : "sidebar-navigation"}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.key}
            to={item.to}
            onClick={onNavClick}
            data-testid={`${prefix}nav-link-${item.key}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-emerald-500/15 text-emerald-700"
                  : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
              }`
            }
          >
            <Icon size={17} />
            {item.label}
          </NavLink>
        );
      })}

      <div className="my-1 border-t border-zinc-200" />

      <button
        type="button"
        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-500 transition-colors hover:bg-red-50"
        onClick={() => { if (onNavClick) onNavClick(); onLogout(); }}
        data-testid={mobile ? "mobile-logout-button" : "logout-button"}
      >
        <LogOut size={17} />
        Déconnexion
      </button>
    </nav>
  );
}

export default function AppLayout({ user, onLogout, children }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="theme-whazago min-h-screen overflow-x-hidden bg-background text-foreground" data-testid="whazago-app-theme">
      <div className="mx-auto grid min-h-screen w-full max-w-7xl grid-cols-1 gap-0 md:grid-cols-[260px_1fr]">

        {/* Sidebar */}
        <aside className="border-b border-white/10 bg-black/60 p-4 backdrop-blur-xl sm:p-6 md:border-b-0 md:border-r">

          {/* Mobile header */}
          <div className="flex items-center justify-between md:mb-6 md:block">
            <div data-testid="sidebar-brand-logo-wrap">
              <WhaZagoLogo />
            </div>

            <div className="md:hidden">
              <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-zinc-300 bg-white text-black hover:bg-zinc-100"
                    data-testid="mobile-menu-trigger"
                  >
                    <Menu size={16} />
                    Menu
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 border-zinc-200 bg-white p-0">
                  <SheetTitle className="sr-only" data-testid="mobile-menu-sheet-title">Navigation</SheetTitle>
                  <SheetDescription className="sr-only" data-testid="mobile-menu-sheet-description">Menu principal</SheetDescription>

                  <div className="flex h-full flex-col">
                    {/* Mobile menu header */}
                    <div className="border-b border-zinc-100 px-4 py-4">
                      <WhaZagoLogo />
                    </div>

                    {/* Mobile nav items */}
                    <div className="flex-1 overflow-y-auto px-3 py-3">
                      <SidebarNav user={user} onLogout={onLogout} mobile onNavClick={() => setMenuOpen(false)} />
                    </div>

                    {/* Mobile user info */}
                    <div className="border-t border-zinc-100 px-4 py-3">
                      <p className="truncate text-xs text-zinc-400" data-testid="mobile-user-email">{user?.email}</p>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:block">
            <SidebarNav user={user} onLogout={onLogout} />
          </div>

          <div className="mt-6 hidden rounded-xl border border-white/5 bg-white/[0.03] p-4 md:block">
            <p className="text-xs text-zinc-400" data-testid="current-user-email-label">Connecté</p>
            <p className="mt-1 truncate text-sm font-semibold text-zinc-100" data-testid="current-user-email-value">{user?.email}</p>
          </div>
        </aside>

        {/* Main content */}
        <main className="bg-gradient-to-b from-zinc-950 via-zinc-950 to-black">
          <div className="mx-auto max-w-6xl p-4 sm:p-6 md:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
